import { z } from "zod"
import { type PoolType, PoolSchema, TokenTypeEnum } from "@tutellus/core-contracts-ifaces"
import { ProjectConfigTokenMetadata } from "./ProjectConfigTokenMetadata"

const ProjectConfigPoolValidation = z.object({
  id: z.string({ required_error: "id is required" }),
  name: z.string({ required_error: "name is required" }),
  symbol: z.string({ required_error: "symbol is required" }),
  decimals: z.number({ required_error: "decimals is required" }),
  type: z.nativeEnum(TokenTypeEnum),
  tokens: z.array(z.string({ required_error: "tokens is required" })),
  multiplier: z.number({ required_error: "multiplier is required" }),
  metadata: z.instanceof(ProjectConfigTokenMetadata),
})

export class ProjectConfigPool {
  constructor(
    private readonly _id: string,
    private readonly _name: string,
    private readonly _symbol: string,
    private readonly _decimals: number,
    private readonly _type: TokenTypeEnum,
    private readonly _tokens: string[],
    private readonly _multiplier: number,
    private readonly _metadata: ProjectConfigTokenMetadata,
  ) {}

  static create({
    id,
    name,
    symbol,
    decimals,
    type,
    tokens,
    multiplier,
    metadata,
  }: z.infer<typeof ProjectConfigPoolValidation>) {
    if (type !== TokenTypeEnum.POOL) throw new Error(`Pool "${id}" is not available`)
    if (!metadata) throw new Error(`Pool metadata "${id}" is not available`)

    const { success, error } = ProjectConfigPoolValidation.safeParse({
      id,
      name,
      symbol,
      decimals,
      type,
      tokens,
      multiplier,
      metadata,
    })

    if (!success) throw new Error(error.message)

    return new ProjectConfigPool(id, name, symbol, decimals, type, tokens, multiplier, metadata)
  }

  static createFromAPI({ id, name, symbol, decimals, type, tokens, multiplier, metadata }: PoolType) {
    if (type !== TokenTypeEnum.POOL) throw new Error(`Pool "${id}" is not available`)
    if (!metadata) throw new Error(`Pool metadata "${id}" is not available`)

    const { success, error } = PoolSchema.safeParse({ id, name, symbol, decimals, type, tokens, multiplier, metadata })
    if (!success) throw new Error(error.message)

    return ProjectConfigPool.create({
      id,
      name,
      symbol,
      decimals,
      type,
      tokens,
      multiplier,
      metadata: ProjectConfigTokenMetadata.createFromAPI(metadata),
    })
  }

  get id() { return this._id } // prettier-ignore
  get name() { return this._name } // prettier-ignore
  get symbol() { return this._symbol } // prettier-ignore
  get decimals() { return this._decimals } // prettier-ignore
  get type() { return this._type } // prettier-ignore
  get tokens() { return this._tokens } // prettier-ignore
  get multiplier() { return this._multiplier } // prettier-ignore
  get metadata() { return this._metadata } // prettier-ignore

  serialize() {
    return {
      id: this._id,
      name: this._name,
      symbol: this._symbol,
      decimals: this._decimals,
      type: this._type,
      tokens: this._tokens,
      multiplier: this._multiplier,
      metadata: this._metadata?.serialize(),
    }
  }
}

export type ProjectConfigPoolSerialized = ReturnType<ProjectConfigPool["serialize"]>
